import { Link, useParams } from 'react-router-dom';
import { appConfig } from '../config/appConfig';
import { useContent } from '../context/ContentContext';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { TopicNav } from '../components/TopicNav';
import { RelatedTopics } from '../components/RelatedTopics';
import { FieldBlock } from '../components/FieldBlock';
import { ResourceLinks } from '../components/ResourceLinks';
import {
  findSpecificIssue,
  findSpecificInHighLevel,
  findHighLevelIssue,
  getCategoryLabel,
  getNeighborsInSpecificIssue,
  getRelatedItems,
  getItemBySlug,
  resolveIssueLinks,
} from '../lib/contentUtils';

export function TopicPage() {
  const { hlSlug, specificSlug, itemSlug } = useParams<{
    hlSlug: string;
    specificSlug: string;
    itemSlug: string;
  }>();
  const { data, loading, error, reload } = useContent();

  if (loading) return <LoadingState />;
  if (error || !data) return <ErrorState message={error ?? 'No data'} onRetry={reload} />;

  const resolved =
    hlSlug && specificSlug ? findSpecificIssue(data, hlSlug, specificSlug) : undefined;

  const item =
    resolved?.specific.detailItems.find((i) => i.slug === itemSlug) ??
    (itemSlug ? getItemBySlug(data, itemSlug) : undefined);

  if (!item) {
    return (
      <div className="page">
        <ErrorState message="Entry not found." />
      </div>
    );
  }

  const highLevel =
    resolved?.highLevel ?? findHighLevelIssue(data, item.primaryIssueId);
  const specific =
    resolved?.specific ??
    (highLevel ? findSpecificInHighLevel(highLevel, item.primarySpecificIssueId) : undefined);

  const navContext =
    highLevel && specific
      ? { hlSlug: highLevel.slug, specificSlug: specific.slug }
      : null;

  const { prev, next } =
    specific && navContext
      ? getNeighborsInSpecificIssue(specific, item.id)
      : { prev: null, next: null };

  const relatedItems = getRelatedItems(data, item, appConfig.relatedTopicsLimit);
  const issueLinks = resolveIssueLinks(
    data,
    item.issueLinks,
    specific?.id,
  );

  const position =
    specific?.detailItems.findIndex((i) => i.id === item.id) ?? -1;

  return (
    <div className="page topic-page">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/explore">Explore</Link>
        {highLevel && (
          <>
            <span aria-hidden> / </span>
            <Link to={`/issues/${highLevel.slug}`}>{highLevel.title}</Link>
          </>
        )}
        {highLevel && specific && (
          <>
            <span aria-hidden> / </span>
            <Link to={`/issues/${highLevel.slug}/${specific.slug}`}>{specific.title}</Link>
          </>
        )}
        <span aria-hidden> / </span>
        <span className="breadcrumb-current">{item.title}</span>
      </nav>

      <article className="topic-article">
        <header className="topic-header">
          <p className="page-eyebrow">
            {highLevel?.title ?? 'Archive'}
            {specific ? ` · ${specific.title}` : ''}
            {' · '}
            {getCategoryLabel(item.category)}
          </p>
          <h1>{item.title}</h1>
          {position >= 0 && specific && (
            <p className="topic-position">
              Entry {position + 1} of {specific.detailItems.length} in this subtopic
            </p>
          )}
          {appConfig.showSourceAttribution && item.originalSectionTitle && (
            <p className="topic-source-note">
              Documented from: {item.originalSectionTitle}
            </p>
          )}
        </header>

        <FieldBlock fields={item.fields} />
        <ResourceLinks resources={item.resources} />

        {navContext && (
          <TopicNav
            hlSlug={navContext.hlSlug}
            specificSlug={navContext.specificSlug}
            prev={prev}
            next={next}
          />
        )}
      </article>

      <RelatedTopics
        data={data}
        items={relatedItems}
        issueLinks={issueLinks}
        context={
          navContext
            ? { highLevelSlug: navContext.hlSlug, specificSlug: navContext.specificSlug }
            : undefined
        }
      />

      {highLevel && specific && (
        <div className="topic-back-link">
          <Link to={`/issues/${highLevel.slug}/${specific.slug}`}>
            ← Back to {specific.title}
          </Link>
        </div>
      )}
    </div>
  );
}
